import { RouteEnum } from './../routing/route.enum';
import {
    Inject,
    Injectable
} from '@angular/core';
import { AuthService } from '../services/auth.service';
import { Router } from '@angular/router';
import { RequestCache } from '../common/interceptors/request-cache.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { HttpCancelService } from './http-cancel.service';
import { DialogService } from '@common/dialog/dialog.service';
import { OKTA_AUTH } from "@okta/okta-angular";
import { OktaAuth } from "@okta/okta-auth-js";
import { apiServiceBaseUriAdmin } from '@config';
import { DOCUMENT } from "@angular/common";

/**
 * Service for logging out of Climb.
 * 
 * NOTE: Must live in this package to prevent circular dependencies
 */
@Injectable()
export class LogoutService {

    constructor(
        private httpCancelService: HttpCancelService,
        private authService: AuthService,
        private router: Router,
        private requestCache: RequestCache,
        private modalService: NgbModal,
        private dialogService: DialogService,
        @Inject(OKTA_AUTH) public oktaAuth: OktaAuth,
        @Inject(DOCUMENT) private document: Document
    ) {}

    sessionExpired(): void {
        this.modalService.dismissAll();
        this.dialogService.closeAll();
        this.clearData();
        this.router.navigate([RouteEnum.LoggedOutRoute]);
    }

    async logout(): Promise<void> {
        if (this.authService.isAuthenticatedSaml()) {
            this.clearData();
            this.logoutSaml();
            return;
        }

        if (this.authService.isAuthenticatedOkta()) {
            this.clearData();
            await this.logoutOkta();
            return;
        }

        this.clearData();
        await this.router.navigate([RouteEnum.Login]);

        /**
         * Add operation to EventLoop queue so that background
         * requests are called and successfully canceled
         * using the cancelPendingRequests()
         */
        setTimeout(() => {
            this.httpCancelService.cancelPendingRequests();
        });
    }

    async logoutOkta(): Promise<void> {
        this.oktaAuth.token.revoke(this.authService.getOktaAccessToken());
        this.oktaAuth.token.revoke(this.authService.getOktaRefreshToken());
        await this.oktaAuth.signOut({ 
            clearTokensBeforeRedirect: true, 
            revokeAccessToken: true, 
            revokeRefreshToken: true, 
            postLogoutRedirectUri: window.location.origin + '/' + RouteEnum.Login 
        });
    }

    logoutSaml(): void {
        this.document.location.href = `${apiServiceBaseUriAdmin}api/SAML2/Logout`;
    }

    private clearData(): void {
        this.requestCache.clear();
        this.authService.clearAuthData();
        this.authService.clearSSOData();
        this.authService.clearFeatureFlagData();
        this.authService.clearSessionData();
        this.authService.clearWorkgroupData();
    }
}
